import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { haushaltApi } from '../api';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const MONATE = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

function eur(v) { return (v||0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }); }

export default function HaushaltPage() {
  const navigate = useNavigate();
  const [ansicht, setAnsicht] = useState('uebersicht');
  const [buchungen, setBuchungen] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [konten, setKonten] = useState([]);
  const [auswertung, setAuswertung] = useState(null);
  const [laden, setLaden] = useState(true);
  const [filterMonat, setFilterMonat] = useState(new Date().getMonth() + 1);
  const [filterJahr, setFilterJahr] = useState(new Date().getFullYear());
  const [zeigeFormular, setZeigeFormular] = useState(false);
  const [bearbeiteId, setBearbeiteId] = useState(null);
  const [form, setForm] = useState({ datum: format(new Date(), 'yyyy-MM-dd'), betrag: '', typ: 'ausgabe', beschreibung: '', kategorie_id: '', konto: 'Hauptkonto', notizen: '', wiederkehrend: false, wiederkehrend_intervall: 'monatlich' });
  const [fehler, setFehler] = useState('');
  const csvRef = useRef();

  useEffect(() => { datenLaden(); }, [filterMonat, filterJahr]);

  async function datenLaden() {
    setLaden(true);
    try {
      const [b, k, ko, a] = await Promise.all([
        haushaltApi.buchungen.list({ monat: filterMonat, jahr: filterJahr }),
        haushaltApi.kategorien.list(),
        haushaltApi.konten.list(),
        haushaltApi.auswertung(filterJahr),
      ]);
      setBuchungen(b.data);
      setKategorien(k.data);
      setKonten(ko.data);
      setAuswertung(a.data);
    } catch(e) { console.error(e); }
    setLaden(false);
  }

  async function buchungSpeichern(e) {
    e.preventDefault(); setFehler('');
    try {
      if (bearbeiteId) await haushaltApi.buchungen.update(bearbeiteId, form);
      else await haushaltApi.buchungen.create(form);
      setZeigeFormular(false); setBearbeiteId(null);
      setForm({ datum: format(new Date(), 'yyyy-MM-dd'), betrag: '', typ: 'ausgabe', beschreibung: '', kategorie_id: '', konto: 'Hauptkonto', notizen: '', wiederkehrend: false, wiederkehrend_intervall: 'monatlich' });
      datenLaden();
    } catch(e) { setFehler(e.response?.data?.error || 'Fehler'); }
  }

  async function buchungLoeschen(id) {
    if (!window.confirm('Buchung löschen?')) return;
    await haushaltApi.buchungen.delete(id);
    datenLaden();
  }

  function bearbeiten(b) {
    setForm({ datum: b.datum, betrag: b.betrag, typ: b.typ, beschreibung: b.beschreibung||'', kategorie_id: b.kategorie_id||'', konto: b.konto||'Hauptkonto', notizen: b.notizen||'', wiederkehrend: b.wiederkehrend===1, wiederkehrend_intervall: b.wiederkehrend_intervall||'monatlich' });
    setBearbeiteId(b.id);
    setZeigeFormular(true);
  }

  async function csvImport(e) {
    const datei = e.target.files[0];
    if (!datei) return;
    const fd = new FormData();
    fd.append('datei', datei);
    try {
      const r = await haushaltApi.csvImport(fd);
      alert(`${r.data.importiert} Buchungen importiert!`);
      datenLaden();
    } catch(e) { alert('Fehler beim Import'); }
  }

  const einnahmenMonat = buchungen.filter(b => b.typ === 'einnahme').reduce((s, b) => s + b.betrag, 0);
  const ausgabenMonat = buchungen.filter(b => b.typ === 'ausgabe').reduce((s, b) => s + b.betrag, 0);
  const saldoMonat = einnahmenMonat - ausgabenMonat;

  const inp = { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box', background: 'white' };
  const lbl = { display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '13px' };

  return (
    <div style={{ padding: '16px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>🏦 Haushaltsbuch</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => { csvRef.current.click(); }} style={{ padding: '8px 14px', background: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>📥 CSV Import</button>
          <input ref={csvRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={csvImport} />
          <a href="/api/hh/export/firefly" style={{ padding: '8px 14px', background: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', textDecoration: 'none', color: '#374151' }}>🔥 Firefly Export</a>
          <a href="/api/hh/export/actual" style={{ padding: '8px 14px', background: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', textDecoration: 'none', color: '#374151' }}>💵 Actual Export</a>
          <button onClick={() => { setZeigeFormular(true); setBearbeiteId(null); }} style={{ padding: '8px 14px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>+ Buchung</button>
        </div>
      </div>

      {/* Monatsfilter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filterMonat} onChange={e => setFilterMonat(parseInt(e.target.value))}
          style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: 'white' }}>
          {MONATE.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
        </select>
        <select value={filterJahr} onChange={e => setFilterJahr(parseInt(e.target.value))}
          style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: 'white' }}>
          {[2023,2024,2025,2026,2027].map(j => <option key={j} value={j}>{j}</option>)}
        </select>
        <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '4px' }}>
          {[['uebersicht','📊 Übersicht'],['buchungen','📋 Buchungen'],['charts','📈 Charts'],['einstellungen','⚙️']].map(([v, l]) => (
            <button key={v} onClick={() => setAnsicht(v)}
              style={{ padding: '6px 12px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                background: ansicht === v ? '#6366f1' : 'transparent', color: ansicht === v ? 'white' : '#64748b', fontWeight: ansicht === v ? '600' : '400' }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Karten */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: `Einnahmen ${MONATE[filterMonat-1]}`, wert: einnahmenMonat, farbe: '#10b981', icon: '📥' },
          { label: `Ausgaben ${MONATE[filterMonat-1]}`, wert: ausgabenMonat, farbe: '#ef4444', icon: '📤' },
          { label: 'Saldo', wert: saldoMonat, farbe: saldoMonat >= 0 ? '#10b981' : '#ef4444', icon: '⚖️' },
        ].map((k, i) => (
          <div key={i} style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${k.farbe}` }}>
            <div style={{ fontSize: '20px', marginBottom: '6px' }}>{k.icon}</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: k.farbe }}>{eur(k.wert)}</div>
            <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Übersicht Tab */}
      {ansicht === 'uebersicht' && auswertung && (
        <div>
          <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '15px' }}>📊 Jahresübersicht {filterJahr}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', marginBottom: '12px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#10b981' }}>{eur(auswertung.gesamtEinnahmen)}</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Einnahmen gesamt</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#ef4444' }}>{eur(auswertung.gesamtAusgaben)}</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Ausgaben gesamt</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: auswertung.saldo >= 0 ? '#10b981' : '#ef4444' }}>{eur(auswertung.saldo)}</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Jahressaldo</div>
              </div>
            </div>
          </div>

          {/* Top Ausgaben-Kategorien */}
          {auswertung.katAusgaben?.filter(k => k.summe > 0).length > 0 && (
            <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '15px' }}>🏷️ Top Ausgaben-Kategorien {filterJahr}</h3>
              {auswertung.katAusgaben.filter(k => k.summe > 0).slice(0, 8).map((k, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>{k.icon}</span>
                    <span style={{ fontSize: '14px', fontWeight: '600' }}>{k.name}</span>
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#ef4444' }}>{eur(k.summe)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Buchungen Tab */}
      {ansicht === 'buchungen' && (
        <div>
          {laden ? <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>⏳ Laden...</div>
            : buchungen.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
                <div>Keine Buchungen für diesen Monat</div>
              </div>
            ) : buchungen.map(b => (
              <div key={b.id} style={{ background: 'white', borderRadius: '10px', padding: '14px 16px', marginBottom: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderLeft: `4px solid ${b.typ === 'einnahme' ? '#10b981' : '#ef4444'}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '22px' }}>{b.kategorie_icon || (b.typ === 'einnahme' ? '📥' : '📤')}</span>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>{b.beschreibung || b.konto}</div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '3px' }}>
                      <span style={{ fontSize: '12px', color: '#64748b' }}>{b.datum}</span>
                      {b.kategorie_name && <span style={{ background: (b.kategorie_farbe||'#6366f1')+'20', color: b.kategorie_farbe||'#6366f1', padding: '1px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: '600' }}>{b.kategorie_name}</span>}
                      {b.wiederkehrend === 1 && <span style={{ background: '#dbeafe', color: '#1e40af', padding: '1px 8px', borderRadius: '99px', fontSize: '11px' }}>🔄 {b.wiederkehrend_intervall}</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: '700', fontSize: '16px', color: b.typ === 'einnahme' ? '#10b981' : '#ef4444' }}>
                      {b.typ === 'einnahme' ? '+' : '-'}{eur(b.betrag)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{b.konto}</div>
                  </div>
                  <button onClick={() => bearbeiten(b)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer', padding: '4px 8px' }}>✏️</button>
                  <button onClick={() => buchungLoeschen(b.id)} style={{ background: '#fee2e2', border: 'none', borderRadius: '6px', cursor: 'pointer', padding: '4px 8px' }}>🗑️</button>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Charts Tab */}
      {ansicht === 'charts' && auswertung && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '15px' }}>📊 Einnahmen vs. Ausgaben {filterJahr}</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={auswertung.monate.map((m, i) => ({ name: MONATE[i], Einnahmen: m.einnahmen, Ausgaben: m.ausgaben }))}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={v => eur(v)} />
                <Bar dataKey="Einnahmen" fill="#10b981" radius={[4,4,0,0]} />
                <Bar dataKey="Ausgaben" fill="#ef4444" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {auswertung.katAusgaben?.filter(k => k.summe > 0).length > 0 && (
            <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '15px' }}>🥧 Ausgaben nach Kategorie</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={auswertung.katAusgaben.filter(k => k.summe > 0)} dataKey="summe" nameKey="name" cx="50%" cy="50%" outerRadius={80}>
                    {auswertung.katAusgaben.filter(k => k.summe > 0).map((k, i) => <Cell key={i} fill={k.farbe || '#6366f1'} />)}
                  </Pie>
                  <Tooltip formatter={v => eur(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Einstellungen Tab */}
      {ansicht === 'einstellungen' && (
        <EinstellungenHaushalt kategorien={kategorien} konten={konten} onReload={datenLaden} />
      )}

      {/* Buchungs-Formular Modal */}
      {zeigeFormular && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: '20px' }}>{bearbeiteId ? '✏️ Buchung bearbeiten' : '➕ Neue Buchung'}</h2>
            {fehler && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>{fehler}</div>}
            <form onSubmit={buchungSpeichern} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Typ Buttons */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {[['ausgabe','📤 Ausgabe','#ef4444'],['einnahme','📥 Einnahme','#10b981']].map(([v,l,c]) => (
                  <button key={v} type="button" onClick={() => setForm({...form, typ: v})}
                    style={{ padding: '12px', border: `2px solid ${form.typ === v ? c : '#e2e8f0'}`, borderRadius: '8px', cursor: 'pointer',
                      background: form.typ === v ? c+'15' : 'white', color: form.typ === v ? c : '#374151', fontWeight: form.typ === v ? '700' : '400' }}>
                    {l}
                  </button>
                ))}
              </div>
              <div>
                <label style={lbl}>Betrag (€) *</label>
                <input style={{ ...inp, fontSize: '20px', fontWeight: '700', color: form.typ === 'ausgabe' ? '#ef4444' : '#10b981' }}
                  type="number" step="0.01" min="0" value={form.betrag} onChange={e => setForm({...form, betrag: e.target.value})} placeholder="0,00" required />
              </div>
              <div>
                <label style={lbl}>Beschreibung *</label>
                <input style={inp} value={form.beschreibung} onChange={e => setForm({...form, beschreibung: e.target.value})} placeholder="Wofür?" required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={lbl}>Datum *</label>
                  <input style={inp} type="date" value={form.datum} onChange={e => setForm({...form, datum: e.target.value})} required />
                </div>
                <div>
                  <label style={lbl}>Kategorie</label>
                  <select style={inp} value={form.kategorie_id} onChange={e => setForm({...form, kategorie_id: e.target.value})}>
                    <option value="">-- Keine --</option>
                    {kategorien.filter(k => k.typ === form.typ || k.typ === 'beides').map(k => <option key={k.id} value={k.id}>{k.icon} {k.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={lbl}>Konto</label>
                <select style={inp} value={form.konto} onChange={e => setForm({...form, konto: e.target.value})}>
                  <option value="Hauptkonto">Hauptkonto</option>
                  {konten.map(k => <option key={k.id} value={k.name}>{k.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input type="checkbox" id="wiederkehrend" checked={form.wiederkehrend} onChange={e => setForm({...form, wiederkehrend: e.target.checked})} />
                <label htmlFor="wiederkehrend" style={{ fontSize: '14px', fontWeight: '600' }}>🔄 Wiederkehrende Buchung</label>
              </div>
              {form.wiederkehrend && (
                <select style={inp} value={form.wiederkehrend_intervall} onChange={e => setForm({...form, wiederkehrend_intervall: e.target.value})}>
                  <option value="taeglich">Täglich</option>
                  <option value="woechentlich">Wöchentlich</option>
                  <option value="monatlich">Monatlich</option>
                  <option value="jaehrlich">Jährlich</option>
                </select>
              )}
              <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                <button type="button" onClick={() => { setZeigeFormular(false); setBearbeiteId(null); }}
                  style={{ flex: 1, padding: '12px', background: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Abbrechen</button>
                <button type="submit"
                  style={{ flex: 2, padding: '12px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}>
                  {bearbeiteId ? '💾 Speichern' : '➕ Buchen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function EinstellungenHaushalt({ kategorien, konten, onReload }) {
  const [neueKat, setNeueKat] = useState({ name: '', typ: 'ausgabe', farbe: '#ef4444', icon: '💰' });
  const [neuesKonto, setNeuesKonto] = useState({ name: '', typ: 'girokonto', startguthaben: 0, farbe: '#6366f1' });
  const inp = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', flex: 1 };

  async function katErstellen(e) {
    e.preventDefault();
    await haushaltApi.kategorien.create(neueKat);
    setNeueKat({ name: '', typ: 'ausgabe', farbe: '#ef4444', icon: '💰' });
    onReload();
  }
  async function kontoErstellen(e) {
    e.preventDefault();
    await haushaltApi.konten.create(neuesKonto);
    setNeuesKonto({ name: '', typ: 'girokonto', startguthaben: 0, farbe: '#6366f1' });
    onReload();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px' }}>🏷️ Kategorien</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
          {kategorien.map(k => (
            <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: '#f8fafc', borderRadius: '8px' }}>
              <span>{k.icon}</span>
              <span style={{ flex: 1, fontSize: '14px', fontWeight: '600' }}>{k.name}</span>
              <span style={{ fontSize: '11px', color: k.typ === 'einnahme' ? '#10b981' : '#ef4444' }}>{k.typ}</span>
              <div style={{ width: '14px', height: '14px', borderRadius: '50%', background: k.farbe }} />
              <button onClick={() => haushaltApi.kategorien.delete(k.id).then(onReload)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>🗑️</button>
            </div>
          ))}
        </div>
        <form onSubmit={katErstellen} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input value={neueKat.icon} onChange={e => setNeueKat({...neueKat, icon: e.target.value})} placeholder="Icon" style={{ ...inp, flex: '0 0 50px' }} />
          <input value={neueKat.name} onChange={e => setNeueKat({...neueKat, name: e.target.value})} placeholder="Name" style={inp} required />
          <select value={neueKat.typ} onChange={e => setNeueKat({...neueKat, typ: e.target.value})} style={{ ...inp, flex: '0 0 120px' }}>
            <option value="ausgabe">Ausgabe</option>
            <option value="einnahme">Einnahme</option>
            <option value="beides">Beides</option>
          </select>
          <input type="color" value={neueKat.farbe} onChange={e => setNeueKat({...neueKat, farbe: e.target.value})} style={{ width: '40px', padding: '2px', border: 'none', borderRadius: '4px' }} />
          <button type="submit" style={{ padding: '8px 14px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>+</button>
        </form>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px' }}>🏦 Konten</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
          {konten.map(k => (
            <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: '#f8fafc', borderRadius: '8px' }}>
              <span style={{ flex: 1, fontSize: '14px', fontWeight: '600' }}>{k.name}</span>
              <span style={{ fontSize: '12px', color: '#64748b' }}>{k.typ}</span>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#6366f1' }}>{(k.startguthaben||0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span>
            </div>
          ))}
        </div>
        <form onSubmit={kontoErstellen} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input value={neuesKonto.name} onChange={e => setNeuesKonto({...neuesKonto, name: e.target.value})} placeholder="Kontoname" style={inp} required />
          <select value={neuesKonto.typ} onChange={e => setNeuesKonto({...neuesKonto, typ: e.target.value})} style={{ ...inp, flex: '0 0 130px' }}>
            <option value="girokonto">Girokonto</option>
            <option value="sparkonto">Sparkonto</option>
            <option value="kreditkarte">Kreditkarte</option>
            <option value="bar">Bargeld</option>
          </select>
          <input type="number" step="0.01" value={neuesKonto.startguthaben} onChange={e => setNeuesKonto({...neuesKonto, startguthaben: e.target.value})} placeholder="Startguthaben" style={{ ...inp, flex: '0 0 140px' }} />
          <button type="submit" style={{ padding: '8px 14px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>+</button>
        </form>
      </div>
    </div>
  );
}
